import { Tooltip } from "@medusajs/ui";
import {
  type Ref,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from "react";
import { cn } from "../utils";

const CLAMP_CLASSNAMES = {
  1: "truncate",
  2: "line-clamp-2",
  3: "line-clamp-3"
} as const;

type TruncateLines = keyof typeof CLAMP_CLASSNAMES;

type TruncateTag = "span" | "div" | "p" | "h2" | "h3" | "h4";

export interface TruncateTooltipProps {
  /** The text to render, and - if it doesn't fit - show in full in the tooltip */
  children: ReactNode;
  /** Number of lines before truncating. 1 = single-line ellipsis (default), 2/3 = multi-line clamp */
  lines?: TruncateLines;
  /** HTML element to render. Defaults to "span" */
  as?: TruncateTag;
  /** Additional classes merged with the truncation class */
  className?: string;
  /** Tooltip content, defaults to `children` */
  tooltipContent?: ReactNode;
  /** Side the tooltip renders on. Defaults to "top" */
  side?: "top" | "bottom" | "left" | "right";
}

/**
 * Reads once whether the primary pointer is coarse (a touch screen). Stable for
 * the life of a session, so a lazy initial read is enough - no listener, and no
 * uncontrolled->controlled switch on the tooltip below. Defensive when
 * `matchMedia` is absent (SSR, a jsdom test that hasn't stubbed it).
 */
function useIsCoarsePointer(): boolean {
  const [coarse] = useState(
    () =>
      typeof window !== "undefined" &&
      !!window.matchMedia &&
      window.matchMedia("(pointer: coarse)").matches
  );
  return coarse;
}

/**
 * Renders text truncated to `lines`, and shows the untruncated value in a
 * tooltip - but only when the text is actually clipped, so short text that
 * already fits gets no tooltip.
 *
 * On a fine pointer the tooltip is the usual hover/focus one. A touch screen
 * can't hover, so there it becomes tap-to-reveal: tapping clipped text opens
 * the full value, tapping it again or anywhere else closes it. Without this a
 * phone user has no way to read a title the ellipsis has eaten.
 */
export function TruncateTooltip({
  children,
  lines = 1,
  as = "span",
  className,
  tooltipContent,
  side = "top"
}: TruncateTooltipProps) {
  const ref = useRef<HTMLElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const isCoarse = useIsCoarsePointer();
  // Tap-to-reveal state, only meaningful on a coarse pointer.
  const [tapOpen, setTapOpen] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const checkTruncation = () => {
      setIsTruncated(
        lines === 1
          ? el.scrollWidth > el.clientWidth
          : el.scrollHeight > el.clientHeight
      );
    };

    checkTruncation();
    // A web font can still be loading at mount time. It swaps in after
    // layout, widening the text without resizing this element's own box -
    // which ResizeObserver, watching only the box, never reports.
    document.fonts.ready.then(checkTruncation);

    const resizeObserver = new ResizeObserver(checkTruncation);
    resizeObserver.observe(el);
    return () => resizeObserver.disconnect();
  }, [children, lines]);

  // A tap-opened tooltip closes when the next tap lands elsewhere. Bound only
  // while it is open on a coarse pointer, so a mouse session never pays for it.
  useEffect(() => {
    if (!isCoarse || !tapOpen) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!ref.current?.contains(event.target as Node)) setTapOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [isCoarse, tapOpen]);

  const Component = as;

  // Always render the same <Tooltip><Component></Tooltip> shape, regardless
  // of `isTruncated` - conditionally switching between `element` and
  // `<Tooltip>{element}</Tooltip>` at the root changes the child's type,
  // which makes React unmount and remount the DOM node. That orphans this
  // effect's ResizeObserver (still bound to the old node) and its next
  // notification reports the detached node's 0x0 size, permanently
  // reverting `isTruncated` back to false. Keeping the tree shape constant
  // keeps the node - and the observer watching it - stable; `open` just
  // forces the tooltip closed when there's nothing to show.
  return (
    <Tooltip
      content={tooltipContent ?? children}
      side={side}
      // Fine pointer: let the tooltip manage its own hover/focus (`undefined`).
      // Coarse pointer: drive it from tap state. Either way, never when the text
      // already fits (`false`).
      open={isTruncated ? (isCoarse ? tapOpen : undefined) : false}
    >
      <Component
        // The ref type varies per `as` tag; all variants extend HTMLElement.
        ref={ref as Ref<never>}
        className={cn(CLAMP_CLASSNAMES[lines], className)}
        onClick={
          isCoarse && isTruncated
            ? () => setTapOpen((prev) => !prev)
            : undefined
        }
      >
        {children}
      </Component>
    </Tooltip>
  );
}
